import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  project_interest: "",
  message: "",
};

export default function Contact() {
  const { content } = useContent();
  const site = content.site || {};
  const projects = content.projects || [];
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const headRef = useReveal();
  const formRef = useReveal();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Įveskite vardą, el. paštą ir žinutę.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_interest: form.project_interest || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      setSent(true);
      setForm(initial);
      toast.success("Ačiū — jūsų žinutė gauta.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Įvyko klaida. Bandykite dar kartą arba parašykite tiesiogiai el. paštu."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" className="bg-[#f9f8f6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-[140px] md:pt-[180px] pb-24 md:pb-40">
        <div className="reveal grid grid-cols-1 md:grid-cols-12 gap-8 items-end" ref={headRef}>
          <div className="md:col-span-7">
            <span className="overline">— 04 / Kontaktai</span>
            <h1 className="mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1a1a1a]">
              Pakalbėkime apie
              <br />
              <span className="italic">jūsų erdvę.</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-[#3a3a3a] text-base md:text-lg leading-relaxed font-light max-w-md md:ml-auto">
              Jeigu jums artimas toks požiūris į architektūrą ir kokybę,
              kviečiu susisiekti.
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <form
            ref={formRef}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="reveal md:col-span-7 space-y-8"
            noValidate
          >
            <Field
              id="name"
              label="Vardas"
              value={form.name}
              onChange={update("name")}
              required
              testid="contact-input-name"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field
                id="email"
                label="El. paštas"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                testid="contact-input-email"
              />
              <Field
                id="phone"
                label="Telefonas (nebūtina)"
                value={form.phone}
                onChange={update("phone")}
                testid="contact-input-phone"
              />
            </div>

            <div>
              <label className="overline block mb-3" htmlFor="project_interest">
                Dominantis projektas
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "", name: "Dar neapsisprendžiau" },
                  ...projects.map((p) => ({ id: p.id, name: p.name })),
                ].map((opt) => {
                  const selected = form.project_interest === opt.id;
                  return (
                    <button
                      type="button"
                      key={opt.id || "any"}
                      data-testid={`contact-pill-${opt.id || "any"}`}
                      onClick={() =>
                        setForm((f) => ({ ...f, project_interest: opt.id }))
                      }
                      className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                        selected
                          ? "bg-[#1a1a1a] text-[#f9f8f6] border-[#1a1a1a]"
                          : "bg-transparent text-[#1a1a1a] border-[#1a1a1a]/40 hover:border-[#1a1a1a]"
                      }`}
                    >
                      {opt.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="overline block mb-3" htmlFor="message">
                Papasakokite apie savo projektą *
              </label>
              <textarea
                id="message"
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                required
                placeholder="Keli sakiniai apie sklypą, terminus, ko siekiate…"
                className="w-full bg-transparent border-b border-[#1a1a1a]/30 focus:border-[#1a1a1a] outline-none py-3 text-[#1a1a1a] placeholder:text-[#9a9690] font-light text-base resize-none transition-colors"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60"
              >
                {submitting
                  ? "Siunčiama…"
                  : sent
                  ? "Išsiųsta — ačiū"
                  : "Siųsti žinutę"}
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
              <span className="text-xs text-[#5c5c5c] font-light">
                Atsakau asmeniškai per 2 darbo dienas.
              </span>
            </div>
          </form>

          <aside className="md:col-span-5">
            <div className="border-t border-[#d8d4ce] pt-10 space-y-8">
              <ContactRow
                icon={<MapPin size={16} strokeWidth={1.5} />}
                label="Miestas"
                value={site.city}
              />
              <ContactRow
                icon={<Phone size={16} strokeWidth={1.5} />}
                label="Telefonas"
                value={
                  <a
                    data-testid="contact-phone"
                    href={`tel:${site.phoneHref}`}
                    className="hover:text-[#a66e4b]"
                  >
                    {site.phone}
                  </a>
                }
              />
              <ContactRow
                icon={<Mail size={16} strokeWidth={1.5} />}
                label="El. paštas"
                value={
                  <a
                    data-testid="contact-email"
                    href={`mailto:${site.email}`}
                    className="hover:text-[#a66e4b] break-all"
                  >
                    {site.email}
                  </a>
                }
              />
              <ContactRow
                icon={<Instagram size={16} strokeWidth={1.5} />}
                label="Instagram"
                value={
                  <a
                    data-testid="contact-instagram"
                    href={site.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-[#a66e4b]"
                  >
                    @manopirtis.lt
                  </a>
                }
              />
              <ContactRow
                icon={<Facebook size={16} strokeWidth={1.5} />}
                label="Facebook"
                value={
                  <a
                    data-testid="contact-facebook"
                    href={site.facebook}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-[#a66e4b]"
                  >
                    MANOPIRTIS.LT
                  </a>
                }
              />
            </div>

            <div className="mt-12 p-8 bg-[#efedea] border border-[#e5e2dd]">
              <span className="overline">Dėl projektų sezono</span>
              <p className="mt-4 text-sm text-[#3a3a3a] font-light leading-relaxed">
                {site.seasonNote}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-5">
      <div className="mt-1 text-[#a66e4b]">{icon}</div>
      <div>
        <div className="overline">{label}</div>
        <div className="mt-2 text-base text-[#1a1a1a] font-light">{value}</div>
      </div>
    </div>
  );
}

function Field({ id, label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline block mb-3" htmlFor={id}>
        {label} {required && "*"}
      </label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-[#1a1a1a]/30 focus:border-[#1a1a1a] outline-none py-3 text-[#1a1a1a] placeholder:text-[#9a9690] font-light text-base transition-colors"
      />
    </div>
  );
}
